import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
    Box, Typography, Dialog, IconButton, Skeleton, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { globalGetService } from "../../../utils/globalApiServices";
import { getLeaseInvCardApi } from "../apiService";
import { checkApiStatus } from "../../../utils";
import { useParams } from "react-router";
import "../styles/InvCard.scss";

// ─── Formatters ───────────────────────────────────────────────────────────────
const validCurrencies = new Set(Intl.supportedValuesOf("currency"));
const _fmtCache = {};

const fmtAmt = (currency, amount) => {
    if (amount == null || Number(amount) <= 0) return null;
    if (!validCurrencies.has(currency)) return String(amount);
    _fmtCache[currency] ??= new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return _fmtCache[currency].format(Number(amount));
};

const fmtNum = (n) => (n != null ? new Intl.NumberFormat("en-US").format(n) : "0");

const fmtUsd = (amount) => {
    const n = fmtAmt("USD", amount);
    return n ? `USD ${n}` : null;
};

// Returns "1,234.56 (3)" for a {count, amount} field, or "0" if empty.
const fmtCell = (currency, field) =>
    field?.count ? `${fmtAmt(currency, field.amount) ?? "0.00"} (${field.count})` : "0";

// Returns the USD entry from a by_currency array.
const findUsdEntry = (byCurrency) => (byCurrency || []).find((c) => c.currency === "USD");

// Splits "USD 1,234.56" → smaller currency span + amount text; non-currency strings pass through.
const renderMeta = (meta) => {
    if (!meta) return null;
    const spaceIdx = meta.indexOf(" ");
    if (spaceIdx > 0) {
        const token = meta.slice(0, spaceIdx);
        if (/^[A-Z]{3}$/.test(token)) {
            return (
                <>
                    <Box component="span" className="ic-tile__row-meta-cur">{token} </Box>
                    {meta.slice(spaceIdx + 1)}
                </>
            );
        }
    }
    return meta;
};

// ─── Build tile configs from API data ─────────────────────────────────────────
const buildTiles = (data) => {
    if (!data) return [];

    const totInv      = data.total_invoiced        || {};
    const paid        = data.paid                  || {};
    const draftSent   = data.draft_and_sent        || {};
    const overdueLate = data.overdue_and_paid_late || {};
    const taxes       = data.taxes                 || {};
    const fees        = data.fees_and_penalties    || {};

    const totalUsdAmt   = totInv.amount_by_currency?.USD ?? null;
    const totalCurCount = Object.keys(totInv.amount_by_currency || {}).length;

    const paidUsdEntry    = findUsdEntry(paid.by_currency);
    const sentUsdEntry    = findUsdEntry(draftSent.by_currency);
    const overdueUsdEntry = findUsdEntry(overdueLate.by_currency);
    const taxesUsdEntry   = findUsdEntry(taxes.by_currency);
    const feesUsdEntry    = findUsdEntry(fees.by_currency);

    // Tax types across all currencies
    const taxTypeSet = new Set();
    (taxes.by_currency || []).forEach((c) => Object.keys(c.taxes || {}).forEach((t) => taxTypeSet.add(t)));
    const taxTypeList = [...taxTypeSet];

    // Fees aggregates
    const feesHasData = (fees.by_currency || []).length > 0;
    const feesTotals  = (fees.by_currency || []).reduce(
        (acc, c) => {
            acc.penaltyCount += c.penalty?.count ?? 0;
            acc.mgmtCount    += c.management_fees?.count ?? 0;
            return acc;
        },
        { penaltyCount: 0, mgmtCount: 0 }
    );

    const sentMeta = sentUsdEntry?.sent?.amount
        ? `${fmtUsd(sentUsdEntry.sent.amount)}`
        : null;

    return [
        {
            title: "Total Invoiced",
            accent: "#1565c0",
            badgeType: "icon",
            icon: "📄",
            iconBg: "#dbeafe",
            amountColor: "#1565c0",
            contentType: "single",
            currency: "USD",
            amount: fmtAmt("USD", totalUsdAmt),
            sub: `${fmtNum(totInv.invoice_count)} invoices · ${totalCurCount} cur`,
            modalTitle: "Total Invoiced",
            modalSub: "Invoice amounts by currency",
            modalCols: ["Currency", "Amount"],
            modalRows: Object.entries(totInv.amount_by_currency || {})
                .filter(([, v]) => Number(v) > 0)
                .map(([cur, amt]) => [cur, fmtAmt(cur, amt) ?? "--"]),
        },
        {
            title: "Paid",
            accent: "#2e7d32",
            badgeType: "badge",
            badge: `${paid.currency_count ?? 0} cur`,
            contentType: "rows",
            rows: [
                { dot: "#2e7d32", name: "Fully paid",   amt: fmtNum(paid.fully_paid_total_count),   meta: fmtUsd(paidUsdEntry?.fully_paid?.amount) },
                { dot: "#66bb6a", name: "Partial paid", amt: fmtNum(paid.partial_paid_total_count), meta: fmtUsd(paidUsdEntry?.partial_paid?.amount) },
            ],
            modalTitle: "Paid",
            modalSub: "Fully paid + partial paid, by currency",
            modalCols: ["Currency", "Fully paid", "Partial paid"],
            modalRows: (paid.by_currency || []).map((c) => [
                c.currency,
                fmtCell(c.currency, c.fully_paid),
                fmtCell(c.currency, c.partial_paid),
            ]),
        },
        {
            title: "Draft & Sent",
            accent: "#f57c00",
            badgeType: "icon",
            icon: "📋",
            iconBg: "rgba(245, 124, 0, 0.22)",
            contentType: "rows",
            rows: [
                { dot: "#90caf9", name: "Draft", amt: fmtNum(draftSent.draft_total_count), meta: null },
                { dot: "#f57c00", name: "Sent",  amt: fmtNum(draftSent.sent_total_count),  meta: sentMeta },
            ],
            modalTitle: "Draft & Sent",
            modalSub: "Outstanding by currency",
            modalCols: ["Currency", "Draft", "Sent"],
            modalRows: (draftSent.by_currency || []).map((c) => [
                c.currency,
                fmtCell(c.currency, c.draft),
                fmtCell(c.currency, c.sent),
            ]),
        },
        {
            title: "Overdue & Paid Late",
            accent: "#c62828",
            badgeType: "badge",
            badge: `${overdueLate.currency_count ?? 0} cur`,
            contentType: "rows",
            rows: [
                { dot: "#c62828", name: "Overdue",   amt: fmtNum(overdueLate.overdue_total_count),   meta: fmtUsd(overdueUsdEntry?.overdue?.amount) },
                { dot: "#7b1fa2", name: "Paid late", amt: fmtNum(overdueLate.paid_late_total_count), meta: `avg delay ${overdueLate.average_late_payment_days ?? 0} days` },
            ],
            modalTitle: "Overdue & Paid Late",
            modalSub: "By currency",
            modalCols: ["Currency", "Overdue", "Paid late"],
            modalRows: (overdueLate.by_currency || []).map((c) => [
                c.currency,
                fmtCell(c.currency, c.overdue),
                c.paid_late?.count
                    ? `${c.paid_late.count} inv · avg ${c.paid_late.average_late_payment_days ?? 0}d`
                    : "0",
            ]),
        },
        {
            title: "Taxes",
            accent: "#4527a0",
            badgeType: "badge",
            badge: `${taxes.currency_count ?? 0} cur`,
            contentType: taxTypeList.length > 0 ? "rows" : "empty",
            rows: taxTypeList.slice(0, 2).map((t, i) => ({
                dot: i === 0 ? "#4527a0" : "#7c4dff",
                name: t,
                amt: fmtNum((taxes.by_currency || []).reduce((sum, c) => sum + (c.taxes?.[t]?.count ?? 0), 0)),
                meta: fmtUsd(taxesUsdEntry?.taxes?.[t]?.amount),
            })),
            modalTitle: "Taxes",
            modalSub: "All tax types by currency",
            modalCols: ["Currency", ...taxTypeList],
            modalRows: (taxes.by_currency || []).map((c) => [
                c.currency,
                ...taxTypeList.map((t) => fmtCell(c.currency, c.taxes?.[t])),
            ]),
        },
        {
            title: "Fees & Penalties",
            accent: "#00838f",
            badgeType: "badge",
            badge: `${fees.currency_count ?? 0} cur`,
            contentType: feesHasData ? "rows" : "empty",
            rows: feesHasData ? [
                { dot: "#00838f", name: "Penalty",         amt: fmtNum(feesTotals.penaltyCount), meta: fmtUsd(feesUsdEntry?.penalty?.amount) },
                { dot: "#2e7d32", name: "Management fees", amt: fmtNum(feesTotals.mgmtCount),    meta: fmtUsd(feesUsdEntry?.management_fees?.amount) },
            ] : [],
            modalTitle: "Fees & Penalties",
            modalSub: "By currency",
            modalCols: ["Currency", "Penalty", "Mgmt fees"],
            modalRows: (fees.by_currency || []).map((c) => [
                c.currency,
                fmtCell(c.currency, c.penalty),
                fmtCell(c.currency, c.management_fees),
            ]),
        },
    ];
};

// ─── Currency breakdown modal ─────────────────────────────────────────────────
const CurrencyModal = ({ open, onClose, title, sub, cols, rows, accent }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "ic-modal__paper", style: { '--ic-modal-accent': accent } }}
    >
        <Box className="ic-modal__header" style={{ borderTop: `3px solid ${accent}` }}>
            <Box>
                <Typography className="ic-modal__title" style={{ color: accent }}>{title}</Typography>
                <Typography className="ic-modal__sub">{sub}</Typography>
            </Box>
            <IconButton size="small" onClick={onClose} className="ic-modal__close-btn">
                <CloseRoundedIcon sx={{ fontSize: "16px" }} />
            </IconButton>
        </Box>
        <TableContainer className="ic-modal__table-wrap">
            <Table className="mui-table-format" size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {cols.map((col, i) => (
                            <TableCell key={i} align={i === 0 ? "left" : "right"} className="ic-modal__cell-head">{col}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={cols.length} align="center" className="ic-modal__empty-cell">No data available</TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row, ri) => (
                            <TableRow key={ri} className={ri % 2 !== 0 ? "ic-modal__row--stripe" : ""}>
                                {row.map((cell, ci) => (
                                    <TableCell key={ci} align={ci === 0 ? "left" : "right"} className={ci === 0 ? "ic-modal__cell-label" : "ic-modal__cell-val"} >
                                        {ci === 0 ? <Box component="span" className="ic-modal__currency-badge">{cell}</Box> : cell}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    </Dialog>
);

// ─── Tile ─────────────────────────────────────────────────────────────────────
const Tile = ({ config }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const hasModal = config.modalRows && config.modalRows.length > 0;

    return (
        <>
            <Box className="ic-tile" style={{ borderTop: `3px solid ${config.accent}`, '--ic-accent': config.accent }}>

                {/* Header */}
                <Box className="ic-tile__header">
                    <Typography className="ic-tile__title">{config.title}</Typography>
                    {config.badgeType === "icon" ? <Typography className="ic-tile__icon" style={{ background: config.iconBg }}>{config.icon}</Typography> : <Typography className="ic-tile__badge">{config.badge}</Typography> }
                </Box>

                {/* Single-amount layout (Total Invoiced) */}
                {config.contentType === "single" && (
                    <>
                        <Box className="ic-tile__amount-wrap">
                            {config.currency && (
                                <Box component="span" className="ic-tile__amount-cur">{config.currency}</Box>
                            )}
                            <Typography className="ic-tile__amount" style={{ color: config.accent }}>
                                {config.amount ?? "--"}
                            </Typography>
                        </Box>
                        <Typography className="ic-tile__sub">{config.sub}</Typography>
                    </>
                )}

                {/* Two-row layout */}
                {config.contentType === "rows" && (
                    <Box className="ic-tile__rows">
                        {config.rows.map((row, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <Box className="ic-tile__divider" />}
                                <Box className="ic-tile__row">
                                    <Box className="ic-tile__row-top">
                                        <Box className="ic-tile__row-name-wrap">
                                            <Box className="ic-tile__row-dot" style={{ backgroundColor: row.dot }} />
                                            <Typography className="ic-tile__row-name">{row.name}</Typography>
                                        </Box>
                                        <Tooltip title="Invoice Count" arrow>
                                            <Typography className="ic-tile__row-amt">{row.amt}</Typography>
                                        </Tooltip>
                                    </Box>
                                    {row.meta && (
                                        <Typography className="ic-tile__row-meta" style={{ color: config.accent }}>{renderMeta(row.meta)}</Typography>
                                    )}
                                </Box>
                            </React.Fragment>
                        ))}
                    </Box>
                )}

                {/* Empty state */}
                {config.contentType === "empty" && (
                    <Typography className="ic-tile__empty">--</Typography>
                )}

                {/* Show more */}
                {hasModal && (
                    <Typography className="ic-tile__show-more" onClick={() => setModalOpen(true)}>Show more ›</Typography>
                )}
            </Box>

            <CurrencyModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                accent={config.accent}
                title={config.modalTitle}
                sub={config.modalSub}
                cols={config.modalCols || []}
                rows={config.modalRows || []}
            />
        </>
    );
};

// ─── Skeleton tile ────────────────────────────────────────────────────────────
const TileSkeleton = () => (
    <Box className="ic-tile-skeleton">
        <Box className="ic-tile-skeleton__header">
            <Skeleton variant="text" width="55%" height={13} />
            <Skeleton variant="rounded" width={36} height={16} className="ic-tile-skeleton__badge" />
        </Box>
        <Skeleton variant="text" width="75%" height={26} />
        <Skeleton variant="text" width="55%" height={13} className="ic-tile-skeleton__sub" />
        <Box className="ic-tile-skeleton__divider" />
        <Skeleton variant="text" width="80%" height={20} className="ic-tile-skeleton__row" />
        <Skeleton variant="text" width="60%" height={12} className="ic-tile-skeleton__row-meta" />
        <Skeleton variant="text" width="40%" height={13} className="ic-tile-skeleton__show-more" />
    </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
const LeaseInvHeaderCards = forwardRef((props, ref) => {
    const { leaseSlug } = useParams();
    const [invCardData, setInvCardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const getInvCardData = () => {
        setIsLoading(true);
        globalGetService(getLeaseInvCardApi({ leaseSlug })).then((response) => {
            if (checkApiStatus(response)) {
                setInvCardData(response?.data?.data);
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        getInvCardData();
    }, []);

    useImperativeHandle(ref, () => ({ refresh: getInvCardData }));

    const tiles = buildTiles(invCardData);

    return (
        <Box className="ic-grid">
            {isLoading
                ? Array.from({ length: 6 }).map((_, i) => <TileSkeleton key={i} />)
                : tiles.map((config, i) => <Tile key={i} config={config} />)}
        </Box>
    );
});

export default LeaseInvHeaderCards;
